import TYPES                      from "@/core/services/Changelogs/types";

import Title_ru_RU                from "@/core/misc/decorators/Title_ru_RU";
import Title_ru_RU_Class          from "@/core/misc/decorators/Title_ru_RU_Class";
import serializeSimplified        from "@/core/misc/decorators/SerializeSimplified";

import Core                       from '@/core/Core';
import DateSimple                 from '@/core/types/DateSimple';
import BaseEntity                 from '@/core/misc/BaseEntity';

import PaymentType                from '@/domains/PaymentRequests/PaymentType';
import PaymentReasonType          from '@/domains/PaymentRequests/PaymentReasonType';
import PaymentRequestPosition     from '@/domains/PaymentRequests/PaymentRequestPosition';
import Invoice                    from '@/domains/PaymentRequests/Invoice';
import PaymentReasonBasis         from '@/domains/PaymentRequests/PaymentReasonBasis';
import CriticalValidationError    from "@/core/misc/CriticalValidationError";
import Partner                    from '@/domains/Partners/Partner';
import Company                    from '@/domains/Companies/Company';
import BankAccount                from '@/domains/BankAccounts/BankAccount';
import Person                     from '@/domains/Persons/Person';
import ProductionSite			        from '@/domains/ProductionSites/ProductionSite';
import SWCostscenter 			        from '@/domains/SWCostscenters/SWCostscenter';
import SWPaymentDestination       from '@/domains/SWPaymentDestinations/SWPaymentDestination';
import SWBudgetItem               from '@/domains/SWBudgetItems/SWBudgetItem';
import Currency                   from '@/domains/Currencies/Currency';
import Order                      from '@/domains/Orders/Order';
import OrderPosition              from '@/domains/Orders/OrderPosition';
import Shipment                   from '@/domains/Shipments/Shipment';
import PaymentPriorityType        from "@/domains/PaymentRequests/PaymentPriorityType";
import ProductCategoriesService   from '@/domains/ProductCategories/ProductCategoriesService';

import Sum                        from "@/core/types/Sum";
import CurrencyRate               from "@/core/types/CurrencyRate"

/**
 * Состояние даты платежа для отображения в ячейке/шапке.
 * modifier — css-класс (error/warning/success), label — подпись под датой (или null).
 * Считается в модели, чтобы не дублировать логику в компонентах.
 */
export type PaymentDateState = {
  modifier: 'error' | 'warning' | 'success' | null;
  label:    string | null;
};

/**
 * Заявка на платёж
 */
@Title_ru_RU_Class('Платеж')              // paymentRequest → PaymentRequest
class PaymentRequest extends BaseEntity {

  static Events = {
    Created: 'payment-request:created',
    Updated: 'payment-request:updated',
  } as const;

  // ---- Флаги ----
  /** Платёж подан вручную, вне ЭТП */
  public isManual: boolean;
  /** Платёж отменён */
  @Title_ru_RU('Отменён', TYPES.BOOLEAN_MARK)
  public isCanceled: boolean;
  /** Платёж завершён */
  public isFinished: boolean;
  /** Базируется на предложении */
  public isOfferBased: boolean;
  /** Продавец за границей */
  public isForeign: boolean = false;
  /** Экспортирован в Oracle */
  public isExportedToSWOracle: boolean;
  /** НДС включен в цену (определяется первой позицией) */
  public isVatIncluded: boolean | null = null;

  // ---- Категория и статус ----
  @serializeSimplified
  public orderCategory: { id: string; name?: string };
  @Title_ru_RU('Статус платежа', TYPES.STRING)
  public lastStatusText: string | null;
  @Title_ru_RU('Приоритет платежа', TYPES.OBJECT)
  @serializeSimplified
  public priorityType: PaymentPriorityType | null;

  // ---- Плательщик ----
  public payer: { inn: string; name: string };
  public buyerCompany: Company;

  // ---- Получатель (с каскадными сеттерами) ----
  private _receiver: Partner;
  private _receiverCompany: Company | null;
  @Title_ru_RU('Р/с контрагента', TYPES.OBJECT, BankAccount.formatDiff)
  private _receiverBankAccount: BankAccount | null;

  // ---- Документы-основания ----
  /** Заказ-основание */
  private _order: Order | null;
  /** Отгрузка-основание */
  @Title_ru_RU('Отгрузка поставщика', TYPES.OBJECT)
  private _shipment: Shipment | null;
  /** Позиция заказа-основание */
  @Title_ru_RU('Позиция заказа', TYPES.OBJECT)
  private _orderPosition: OrderPosition | null;
  /** Счёт от контрагента */
  @Title_ru_RU('Счёт', TYPES.OBJECT, Invoice.formatDiff)
  public invoice: Invoice | null;

  // ---- Описание платежа (формируется из реквизитов) ----
  /** Тип основания платежа (счёт, заказ, отгрузка и т.д.) */
  @Title_ru_RU('Тип основания', TYPES.OBJECT)
  private _reasonType: PaymentReasonType | null;
  /** Процент оплаты полем модели больше не является: он осмыслен только в
   *  момент заполнения формы. Контрол вызывает applyPaymentPercent, который
   *  раскладывает долю по количествам позиций; наружу уходят количества и
   *  суммы, а доля при открытии документа выводится из снимка основания. */
  /** За что платим (категория: сырьё, упаковка и т.д.) — подставляется автоматически по категории позиции */
  @Title_ru_RU('Основание платежа', TYPES.OBJECT, PaymentReasonBasis.formatDiff)
  public reasonBasis: PaymentReasonBasis | null;
  /** Итоговый текст назначения платежа */
  @Title_ru_RU('Текст назначения', TYPES.STRING)
  public reasonText: string = '';
  /** Производственная площадка (с каскадным сеттером) */
  @Title_ru_RU('Производственная площадка', TYPES.OBJECT)
  @serializeSimplified
  private _productionSite: ProductionSite | null;
  /** ЦФО платежа — предустанавливается из площадки, может быть переопределён */
  @Title_ru_RU('ЦФО', TYPES.OBJECT)
  @serializeSimplified
  public swCostscenter: SWCostscenter | null;
  /** Назначение платежа Oracle (с каскадным сеттером) */
  @Title_ru_RU('Назначение платежа Oracle', TYPES.OBJECT)
  @serializeSimplified
  private _swPaymentDestination: SWPaymentDestination | null;
  /** Статья бюджета — определяется на основании выбранного swPaymentDestination */
  @Title_ru_RU('Статья бюджета', TYPES.OBJECT)
  @serializeSimplified
  public swBudgetItem: SWBudgetItem | null;

  // ---- Внешние номера ----
  /** Номер в КСЗ (Oracle) */
  public externalNumber: string | null;

  // ---- Валюта сделки ----
  private _orderCurrencyCode: string;
  @Title_ru_RU('Полная сумма', TYPES.SUM)
  public sumFull: Sum;
  @Title_ru_RU('Сумма НДС', TYPES.SUM)
  public vatSum: Sum;

  // ---- Тип и параметры платежа ----
  private _paymentType: PaymentType;
  private _paymentRequestCurrencyCode: string;
  @Title_ru_RU('Курс в заявке на платёж', TYPES.SUM)
  private _paymentRequestCurrencyRate: CurrencyRate;
  @Title_ru_RU('Сумма в заявке на платёж', TYPES.SUM)
  public paymentRequestSumFull: Sum;
  @Title_ru_RU('НДС в заявке на платёж', TYPES.SUM)
  public paymentRequestVatSum: Sum;
  public plannedPaymentDate: DateSimple;

  // ---- Фактический платёж ----
  /** Значение ставит конструктор, из страны партнёра либо из конфига. */
  public actualPaymentCurrencyCode: string;
  public actualPaymentCurrencyRate: CurrencyRate | null;
  @Title_ru_RU('Полная сумма оплаты факт.', TYPES.SUM)
  public actualPaymentSumFull: Sum | null;
  @Title_ru_RU('НДС факт.', TYPES.NUMBER)
  public actualPaymentVatSum: Sum | null;
  @Title_ru_RU('Дата оплаты факт.', TYPES.DATE)
  public actualPaymentDate: DateSimple | null;

  // ---- Прочее ----
  @Title_ru_RU('Ответственный', TYPES.OBJECT)
  public responsible: Person;
  @Title_ru_RU('Комментарий', TYPES.STRING)
  public commentary: string | null;
  @Title_ru_RU('Дата плана производства', TYPES.DATE)
  public manufactureDate: DateSimple | null;

  // ---- Позиции ----
  @Title_ru_RU('Позиции', TYPES.STRING)
  public positions: PaymentRequestPosition[] = [];

  constructor() {
    super();
    /** Локальная валюта площадки. Страна партнёра — правильный источник, но
     *  кода валюты в ней может не оказаться, поэтому за ней конфиг. Тот же
     *  приём, что в Order.accountingCurrency. */
    this.actualPaymentCurrencyCode = Core.currentPartner?.country?.localCurrency?.code
        ?? Core.config.localCurrency.code;
  }

  static fromServer(data: any, result = new PaymentRequest()): PaymentRequest {
    super.fromServer(data, result);

    result.priorityType = Core.utils.hydrate(data.priorityType, PaymentPriorityType.fromServer);

    // Получатель
    result._receiver            = Core.utils.hydrate(data.receiver, Partner.fromServer)!;
    result._receiverCompany     = Core.utils.hydrate(data.receiverCompany, Company.fromServer)!;
    result._receiverBankAccount = Core.utils.hydrate(data.receiverBankAccount, BankAccount.fromServer);

    // Плательщик
    result.buyerCompany = Core.utils.hydrate(data.buyerCompany, Company.fromServer)!;

    // Документы-основания
    result._order         = Core.utils.hydrate(data.order, Order.fromServer);
    result._shipment      = Core.utils.hydrate(data.shipment, Shipment.fromServer);
    result._orderPosition = Core.utils.hydrate(data.orderPosition, OrderPosition.fromServer);

    // Счёт
    if (data.invoice || data.invoiceNumber) {
      result.invoice = Invoice.fromServer({
        number:       data.invoice?.number || data.invoiceNumber,
        date:         data.invoice?.date || data.invoiceDate,
        currencyCode: data.invoice?.currencyCode || data.invoiceCurrencyCode,
        sum:          data.invoice?.sum ?? data.invoiceSum ?? null,
      });
    }

    // Описание платежа — инициализируем приватные поля напрямую,
    // минуя каскадные сеттеры (данные с сервера уже консистентны)
    result._reasonType           = Core.utils.hydrate(data.reasonType, PaymentReasonType.fromServer);
    result.reasonBasis           = Core.utils.hydrate(data.reasonBasis, PaymentReasonBasis.fromServer);
    result.reasonText            = data.reasonText ?? '';
    result._productionSite       = Core.utils.hydrate(data.productionSite, ProductionSite.fromServer);
    result.swCostscenter         = Core.utils.hydrate(data.swCostscenter, SWCostscenter.fromServer);
    result._swPaymentDestination = Core.utils.hydrate(data.swPaymentDestination, SWPaymentDestination.fromServer);
    result.swBudgetItem          = Core.utils.hydrate(data.swBudgetItem, SWBudgetItem.fromServer);

    // Тип платежа
    result._paymentType = Core.utils.hydrate(data.paymentType, PaymentType.fromServer)!;

    // Валюта
    result._orderCurrencyCode          = data.orderCurrencyCode;
    result._paymentRequestCurrencyCode = data.paymentRequestCurrencyCode;

    result.sumFull                     = Core.utils.hydrate(data.sumFull, Sum.fromServer);
    result.vatSum                      = Core.utils.hydrate(data.vatSum, Sum.fromServer);
    result.paymentRequestSumFull       = Core.utils.hydrate(data.paymentRequestSumFull, Sum.fromServer);
    result.paymentRequestVatSum        = Core.utils.hydrate(data.paymentRequestVatSum, Sum.fromServer);
    result._paymentRequestCurrencyRate = Core.utils.hydrate(data.paymentRequestCurrencyRate, CurrencyRate.fromServer);
    result.actualPaymentCurrencyRate   = Core.utils.hydrate(data.actualPaymentCurrencyRate, CurrencyRate.fromServer);
    result.actualPaymentSumFull        = Core.utils.hydrate(data.actualPaymentSumFull, Sum.fromServer);
    result.actualPaymentVatSum         = Core.utils.hydrate(data.actualPaymentVatSum, Sum.fromServer);

    // Ответственный
    result.responsible = Core.utils.hydrate(data.responsible, Person.fromServer)!;

    // Даты
    result.plannedPaymentDate = Core.utils.hydrate(data.plannedPaymentDate, DateSimple.fromServer)!;
    result.actualPaymentDate  = Core.utils.hydrate(data.actualPaymentDate, DateSimple.fromServer);
    result.manufactureDate    = Core.utils.hydrate(data.manufactureDate, DateSimple.fromServer);

    // Позиции (в конструктор позиции важно передать саму PaymentRequest,
    // чтобы не делать запрос к бэку за полным объектом)
    result.positions = Core.utils.hydrateArray(data.positions, (item: any) => {
      const position = PaymentRequestPosition.fromServer(item);

      position.setPaymentRequest(result);

      /** Платёж загружен в собственном контексте — сервер посчитал
       *  агрегаты статуса с учётом его суммы, исключаем её.
       *  Признак проведённости — тот же isPaid, что видит остальной код
       *  (в т.ч. прогресс-бар оплаты): сравнение только с «Платеж проведен»
       *  расходилось с ним на «Подготавливается экспорт в УУ» и на платежах
       *  с фактической датой оплаты. actualPaymentDate к этому моменту уже
       *  гидрирован — см. блок «Даты» выше. */
      position.orderPosition.paymentStatus.excludePayment(result.isPaid);

      return position;
    });

    return result;
  }

  static fromNew(result = new PaymentRequest()): PaymentRequest {
    result.responsible = Core.currentUser as Person;

    /** Суммы нового платежа — нули: позиций ещё нет, агрегат по пустому
     *  списку равен нулю. Курс заявки — пусто: он не «ноль», он не введён
     *  (тип byCustomRate ждёт ввода пользователем). Фактический платёж —
     *  null: события оплаты не было. */
    result.sumFull               = Sum.zero();
    result.vatSum                = Sum.zero();
    result.paymentRequestSumFull = Sum.zero();
    result.paymentRequestVatSum  = Sum.zero();

    result._paymentRequestCurrencyRate = CurrencyRate.fromEmpty();

    result.actualPaymentCurrencyRate = null;
    result.actualPaymentSumFull      = null;
    result.actualPaymentVatSum       = null;
    result.actualPaymentDate         = null;

    return result;
  }

  // ============================================================
  // Сеттеры/геттеры получателя
  // ============================================================

  @serializeSimplified
  get receiver(): Partner {
    return this._receiver;
  }

  set receiver(value: Partner) {
    if (value !== this._receiver) {
      this._receiverCompany   = null;
      this._receiverBankAccount = null;
    }

    this._receiver = value;

    // Если у партнёра одна компания — выбираем автоматически
    if (this._receiver.companies?.length === 1) {
      this.receiverCompany = this._receiver.companies[0];
    }
  }

  @serializeSimplified
  get receiverCompany(): Company | null {
    return this._receiverCompany;
  }

  set receiverCompany(value: Company | null) {
    this._receiverCompany = value;

    if (!value) {
      this._receiverBankAccount = null;
      return;
    }

    // Юр. лицо не из страны текущего партнёра — получатель иностранный
    this.isForeign = value.country.code !== Core.currentPartner.country.code;

    // Если у компании один р/с — выбираем автоматически
    const bankAccounts = value.bankAccounts ?? [];
    if (bankAccounts.length === 1) {
      this.receiverBankAccount = bankAccounts[0];
    } else {
      // Или выбираем основной
      const mainAccount = bankAccounts.find((acc: any) => acc.isMain);
      if (mainAccount) {
        this.receiverBankAccount = mainAccount;
      }
    }
  }

  @serializeSimplified
  get receiverBankAccount(): BankAccount | null {
    return this._receiverBankAccount;
  }

  set receiverBankAccount(value: BankAccount | null) {
    this._receiverBankAccount = value;
  }

  // ============================================================
  // Сеттеры/геттеры оснований (order, shipment, orderPosition)
  // ============================================================

  @serializeSimplified
  get reasonType(): PaymentReasonType | null {
    return this._reasonType;
  }

  set reasonType(value: PaymentReasonType | null) {
    /** Документы-основания здесь больше не сбрасываются. Сброс шёл по любой
     *  смене типа, и при переключении «Отгрузка» → «Счёт поставщика по
     *  отгрузке» выбранная отгрузка слетала, хотя новому типу она нужна
     *  ровно так же. Заполнять её заново — главное, на что жаловались.
     *
     *  Лишний документ никуда не денется: позиции пересобираются из того,
     *  который требует текущий тип, а на сервер уходит только он (toJSON). */
    this._reasonType = value;

    /** Основание требует реквизитов счёта — форма биндится на поля invoice,
     *  объект обязан существовать до рендера контролов. Уже заполненный
     *  счёт не перезатираем: смена основания в пределах invoice-типов
     *  реквизиты не обнуляет. */

    if (value !== null && value.requiresInvoice) {
      if (this.invoice === null || this.invoice === undefined) {
        this.invoice = Invoice.fromNew();
      } else {
        // счёт уже заполнен — оставляем как есть
      }
    } else {
      this.invoice = null;
    }

    // Валюта сделки — по документу нового основания, позиции пересоберёт её сеттер
    this.orderCurrencyCode = this.getBasisCurrencyCode(value);

    this.updateReasonText();
  }

  /** Валюта сделки по документу, которого требует тип основания, либо null */
  private getBasisCurrencyCode(type: PaymentReasonType | null | undefined): string | null {
    if (type?.requiresShipment === true) {
      return this._shipment?.orderCurrencyCode ?? null;
    }
    else if (type?.requiresOrder === true) {
      return this._order?.currencyCode ?? null;
    }
    else if (type?.requiresOrderPosition === true) {
      return this._orderPosition?.currencyCode ?? null;
    }
    else {
      return null;
    }
  }

  @serializeSimplified
  get order(): Order | null {
    return this._order;
  }

  set order(value: Order | null) {
    this._order = value;

    if (!value) return;

    this.isOfferBased  = value.isOfferBased;
    this.orderCategory = value.orderCategory;

    /** Валюта сделки берётся с самого заказа. У мультивалютного она пуста,
     *  и это верно: заказ не знает, в какой из своих валют мы платим.
     *
     *  Раньше здесь читался value.currencyList, которого на Order нет вовсе,
     *  поэтому валюта сделки не проставлялась никогда — ни одновалютному
     *  заказу, ни какому. Отсюда и «не указан курс оплаты» с бэка: весь
     *  каскад типа оплаты и курса строился на пустой валюте.
     *
     *  У мультивалютного заказа валюту выбирают в форме из разбивки
     *  order.positionsByCurrency; позиции соберутся после её выбора. */
    if (Core.utils.isNullOrUndefined(value.currencyCode) === false) {
      this.orderCurrencyCode = value.currencyCode;
    }

    this.updateReasonText();
  }

  @serializeSimplified
  get shipment(): Shipment | null {
    return this._shipment;
  }

  set shipment(value: Shipment | null) {
    this._shipment = value;

    if (!value) return;

    this.isOfferBased      = value.isOfferBased;
    this.isVatIncluded     = value.isVatIncluded ?? null;
    this.orderCategory     = value.orderCategory;
    this.orderCurrencyCode = value.orderCurrencyCode;

    // Площадка из первой позиции отгрузки
    const firstPosition = value.positions?.[0];
    if (firstPosition?.orderPosition?.buyerProductionSite) {
      this.productionSite = firstPosition.orderPosition.buyerProductionSite;
    }

    this.updateReasonText();
  }

  @serializeSimplified
  get orderPosition(): OrderPosition | null {
    return this._orderPosition;
  }

  set orderPosition(value: OrderPosition | null) {
    this._orderPosition = value;

    if (!value) return;

    this.isOfferBased      = value.isOfferBased;
    this.isVatIncluded     = value.isVatIncludedSeller ?? null;
    this.orderCategory     = value.orderCategory;
    this.orderCurrencyCode = value.currencyCode;

    if (value?.buyerProductionSite) {
      this.productionSite = value.buyerProductionSite;
    }

    this.updateReasonText();
  }

  // ============================================================
  // Сеттеры/геттеры описания платежа
  // ============================================================

  @serializeSimplified
  get productionSite(): ProductionSite | null {
    return this._productionSite;
  }

  set productionSite(value: ProductionSite | null) {
    /** Площадка та же, меняется только объект - это дозагрузка огрызка
     *  {id, name} до полного, а не выбор пользователя. Каскад тут не нужен и
     *  вреден: loadSwCostscenterOptions подменяет площадку по preloadOnFocus,
     *  и на существующем документе первый же клик в селект ЦФО обнулял бы
     *  ЦФО, назначение и статью, пришедшие из базы. */
    const isSameSite = value !== null
        && this._productionSite !== null
        && this._productionSite !== undefined
        && value.id === this._productionSite.id;

    this._productionSite = value;

    if (isSameSite === true) {
      return;
    }

    // Автоподстановка ЦФО из площадки
    this.swCostscenter = value?.swCostscenter ?? null;

    // Сбрасываем зависимые поля
    this.swPaymentDestination = null;
  }

  @serializeSimplified
  get swPaymentDestination(): SWPaymentDestination | null {
    return this._swPaymentDestination;
  }

  set swPaymentDestination(value: SWPaymentDestination | null) {
    this._swPaymentDestination = value;

    // Сбрасываем статью при смене назначения
    this.swBudgetItem = null;
  }

  // ============================================================
  // Сеттеры/геттеры валюты, типа, курса
  // ============================================================

  get orderCurrencyCode(): string {
    return this._orderCurrencyCode;
  }

  set orderCurrencyCode(value: string) {
    this._orderCurrencyCode = value;

    if (Core.utils.isNullOrUndefined(value) === true) {
      /** Валюта сделки неизвестна — тип оплаты выводить не из чего, его
       *  задаст выбор валюты. Позиции прежней валюты убираем. */
      this.rebuildPositions();
    }
    // Если валюта сделки = локальная, ставим тип "Прямой"
    else if (this._orderCurrencyCode === this.actualPaymentCurrencyCode) {
      this.paymentType = PaymentType.DIRECT;
      this.applyLocalInvoiceCurrency();
    } else {
      // Иначе по умолчанию "По курсу ЦБ"
      this.paymentType = PaymentType.BY_RATE_OF_CENTRAL_BANK;
    }
  }

  /**
   * Валюта сделки локальная — значит и счёт выставлен в ней же, выбирать
   * нечего.
   *
   * Форма в этом случае гасит поле валюты счёта, но поле обязательное:
   * недоступное и пустое одновременно — платёж не сохранить вовсе, форма
   * требует заполнить то, что сама же и запретила трогать. Поэтому значение
   * не просто запрещаем менять, а проставляем.
   *
   * Затирание ранее выбранного здесь уместно: правило жёсткое, а исправить
   * вручную пользователь всё равно не сможет — поле недоступно.
   */
  private applyLocalInvoiceCurrency(): void {
    if (Core.utils.isNullOrUndefined(this.invoice) === true) {
      // Основание без счёта — подставлять некуда
    }
    else {
      this.invoice!.currencyCode = this._orderCurrencyCode;
      this.invoice!.currency     = Core.utils.money.resolveCurrency(this._orderCurrencyCode);
    }
  }

  get paymentType(): PaymentType {
    return this._paymentType;
  }

  set paymentType(value: PaymentType) {
    this._paymentType = value;

    switch (value.id) {
      case 'byRateOfCentralBank':
        // Платим в валюте сделки/счёта, курс 1:1
        this._paymentRequestCurrencyCode = this.invoice?.currencyCode ?? this._orderCurrencyCode;
        this._paymentRequestCurrencyRate = new CurrencyRate(1);
        break;

      case 'byCustomRate':
        /** Платим в локальной валюте, курс выводится из суммы счёта: её
         *  отношения к сумме основания платежа.
         *
         *  До этого курс ПУСТОЙ, а не единица: единица — валидное значение,
         *  с ним платёж выглядит посчитанным, хотя валютная сумма просто
         *  переименована в локальную. */
        this._paymentRequestCurrencyCode = this.actualPaymentCurrencyCode;
        this._paymentRequestCurrencyRate = CurrencyRate.fromEmpty();
        this.applyCurrencyRateFromInvoice();
        break;

      case 'byDocumentExactly':
        // Берём суммы из документа-основания как есть
        this._paymentRequestCurrencyCode = this.actualPaymentCurrencyCode;
        if (this._shipment) {
          this._paymentRequestCurrencyRate = this._shipment.accountingCurrencyRate;
        }
        break;

      case 'direct':
        // Валюта сделки и так локальная — конвертировать нечего
        this._paymentRequestCurrencyCode = this.actualPaymentCurrencyCode;
        this._paymentRequestCurrencyRate = new CurrencyRate(1);
        break;

      default:
        /** Раньше здесь молча отрабатывал direct. Неявный дефолт для типа
         *  оплаты недопустим: неизвестное значение означало бы курс 1 и
         *  локальную валюту, то есть валютную сумму приняли бы за рублёвую. */
        throw new CriticalValidationError(
            `Неизвестный тип оплаты: ${value.id}`,
            { paymentRequestId: this.id, paymentTypeId: value.id },
        );
    }

    // Пересчитываем позиции
    this.rebuildPositions();
  }

  get paymentRequestCurrencyCode(): string {
    return this._paymentRequestCurrencyCode;
  }

  set paymentRequestCurrencyCode(value: string) {
    this._paymentRequestCurrencyCode = value;
  }

  get paymentRequestCurrencyRate(): CurrencyRate {
    return this._paymentRequestCurrencyRate;
  }

  set paymentRequestCurrencyRate(value: CurrencyRate) {
    /** isPositive отсекает разом все негодные значения: ноль,
     *  отрицательное и пустое (NaN > 0 → false). */
    if (value.isPositive()) {
      this._paymentRequestCurrencyRate = value;

      // Пересчитываем позиции под новый курс. Сеттер курса в позиции сам
      // дёргает calculatePaymentRequest и, через него, recalculateSums.
      for (const position of this.positions) {
        position.paymentRequestCurrencyRate = value;
      }
      this.recalculateSums();
    } else {
      Core.error(`Некорректный курс валюты: ${value}`);
    }
  }

  // ============================================================
  // Computed
  // ============================================================

  /** Валюта сделки совпадает с валютой платежа */
  get isSameCurrency(): boolean {
    return this._orderCurrencyCode === this._paymentRequestCurrencyCode;
  }

  /**
   * Платёж облагается НДС. Определяется позициями (ставка первой —
   * addPosition гарантирует однородность), а не receiverCompany:
   * источник правды о позициях — сами позиции.
   *
   * isPositive, а не отрицание isZero: пустая ставка не ноль, но и НДС
   * по ней не считается — внутри greaterThan(0), на NaN это false.
   */
  get hasVat(): boolean {
    return this.positions[0]?.vatRateSeller?.isPositive() === true;
  }

  /** Валюта сделки — локальная */
  get isLocalOrderCurrency(): boolean {
    return this._orderCurrencyCode === this.actualPaymentCurrencyCode;
  }

  /** Платёж оплачен */
  get isPaid(): boolean {
    return this.actualPaymentDate != null ||
        ['Платеж проведен', 'Подготавливается экспорт в УУ'].includes(this.lastStatusText ?? '');
  }

  /** Платёж в обработке */
  get isProcessing(): boolean {
    return ['На оплате у бухгалтера', 'Отправлен на обработку в банк'].includes(this.lastStatusText ?? '');
  }

  /** Можно ли редактировать */
  get isEditable(): boolean {
    return !this.isPaid && !this.isCanceled && !this.isProcessing;
  }

  /** Причина недоступности редактирования */
  get editDisabledReason(): string|null {
    if (this.isCanceled) return 'Платёж уже отменён';
    if (this.isPaid) return 'Платёж уже оплачен';
    if (this.isProcessing) return 'Платёж в обработке';
    return null;
  }

  /** Можно ли отменить */
  get canBeCanceled(): { allowed: boolean; reason?: string } {
    if (!this.id) {
      return {allowed: false, reason: 'Платёж ещё не создан'};
    }
    if (this.isCanceled) {
      return {allowed: false, reason: 'Платёж уже отменён'};
    }
    if (this.isPaid) {
      return {allowed: false, reason: 'Платёж уже оплачен'};
    }
    if (this.isProcessing) {
      return {allowed: false, reason: 'Платёж в обработке'};
    }
    return {allowed: true};
  }

  /** Площадка является наманганской */
  get isNamanganSite(): boolean {
    return this._productionSite?.id?.startsWith('nmg_') ?? false;
  }

  /** Есть ли основание платежа */
  get hasBasis(): boolean {
    return this.reasonBasis !== null && this.reasonBasis !== undefined;
  }

  /**
   * Есть ли ненулевая сумма.
   * Сравнение объекта с числом (!== 0) коэрции не делает и было бы истинно
   * всегда — состояние спрашивается у самого значения.
   */
  get hasSum(): boolean {
    return !this.paymentRequestSumFull.isEmpty() && !this.paymentRequestSumFull.isZero();
  }

  /**
   * Причина, по которой позицию удалить нельзя, или null, если можно.
   *
   * Раньше кнопка удаления висела на canBeCanceled - предикате отмены всего
   * платежа, который начинается с «Платёж ещё не создан». У нового документа
   * id нет, поэтому удаление позиций было заблокировано до первого сохранения.
   */
  get reasonUnavailableDeletePosition(): string | null {
    if (this.id === null || this.id === undefined) {
      return null;
    }
    else {
      return this.editDisabledReason;
    }
  }

  /**
   * Причина, по которой позиции добавить нельзя, или null, если можно.
   * Имя и форма — как у reasonUnavailableEdit и reasonUnavailableDelete
   * в Shipment: текст идёт прямо в title кнопки.
   */
  get reasonUnavailableAddPositions(): string | null {
    if (this.reasonType === null || this.reasonType === undefined) {
      return 'Не выбрано основание платежа';
    }
    if (this.reasonType.isStandaloneInvoice === false) {
      return `Позиции подбираются автоматически по основанию «${this.reasonType.name}»`;
    }
    return null;
  }

  /**
   * Валюта платежа отличается от валюты сделки — в гриде под ценой и суммой
   * показывается подстрочник в валюте сделки.
   *
   * Раньше «по курсу ЦБ» выходил отсюда досрочно с ложью, и подстрочники
   * пропадали. Это неверно: при этом типе валюта платежа берётся из счёта,
   * а счёт в валюте сделки быть не обязан — как раз тот случай, когда
   * подстрочник нужнее всего. Признак описывает расхождение валют, а не тип
   * оплаты, поэтому сравнения кодов достаточно.
   */
  get hasCurrencyDifference(): boolean {
    return this._orderCurrencyCode !== this._paymentRequestCurrencyCode;
  }

  // ============================================================
  // Представление данных
  // ============================================================

  /** Объект валюты сделки */
  get orderCurrency(): Currency | null {
    return Core.utils.money.resolveCurrency(this._orderCurrencyCode ?? null);
  }

  /**
   * Парный сеттер. Нужен не ради удобства: VSelectV2 сбрасывает зависимый
   * селект программно (смена sourceDependencyKey при выборе основания), и без
   * сеттера запись через реактивный прокси отваливается с "proxy set handler
   * returned false".
   *
   * Тело тонкое намеренно - весь каскад по смене валюты живёт в сеттере
   * orderCurrencyCode, дублировать его здесь нельзя.
   */
  set orderCurrency(value: Currency | null) {
    this.orderCurrencyCode = value?.code ?? null;
  }

  /**
   * Валюты, доступные для сделки/счёта: [локальная валюта получателя,
   * валюта сделки]. Валюта сделки известна, когда есть позиции или
   * сущность-основание (сеттеры order/shipment/orderPosition ведут
   * _orderCurrencyCode); пока её нет — ограничивать нечем, доступны
   * все валюты.
   */
  get availableCurrencies(): Currency[] {
    const all = Array.from(Core.currencies?.currencies?.values() ?? []) as Currency[];

    if (!this._orderCurrencyCode) {
      return all;
    }

    const localCode = this.receiver?.country?.localCurrency?.code;

    return all.filter(currency =>
        currency.code === localCode || currency.code === this._orderCurrencyCode,
    );
  }

  /** Объект валюты платежа */
  get paymentRequestCurrency(): Currency | null {
    return Core.utils.money.resolveCurrency(this._paymentRequestCurrencyCode ?? null);
  }

  /** Парный сеттер, см. orderCurrency */
  set paymentRequestCurrency(value: Currency | null) {
    this.paymentRequestCurrencyCode = value?.code ?? null;
  }

  /** Объект валюты фактического платежа */
  get actualPaymentCurrency(): Currency | null {
    return Core.utils.money.resolveCurrency(this.actualPaymentCurrencyCode ?? null);
  }

  /** Текст статуса с fallback */
  get statusText(): string {
    return this.lastStatusText ?? '--';
  }

  /**
   * Текст статуса для отображения: отменён/удалён/ожидает экспорта важнее
   * текущего lastStatusText, поэтому проверяются первыми.
   */
  get displayStatusText(): string {
    if (this.isCanceled) {
      return 'Отменён';
    }
    else if (this.isDeleted) {
      return 'Удалён';
    }
    else if (!this.isExportedToSWOracle) {
      return 'Ожидает экспорта';
    }
    else {
      return this.lastStatusText ?? '--';
    }
  }

  /**
   * CSS-модификатор статуса для отображения.
   * critical — отменён/удалён, success — проведён, proceed — в процессе.
   */
  get displayStatusModifier(): 'critical' | 'success' | 'proceed' | null {
    if (this.isCanceled || this.isDeleted) {
      return 'critical';
    }
    else if (this.lastStatusText === 'Платеж проведен') {
      return 'success';
    }
    else if (this.isExportedToSWOracle) {
      return 'proceed';
    }
    else {
      return null;
    }
  }

  /**
   * Платёж просрочен: фактическая дата оплаты позже плановой.
   * Если одной из дат нет — не просрочен.
   */
  get isOverdue(): boolean {
    if (!this.plannedPaymentDate || !this.actualPaymentDate) {
      return false;
    }

    return this.actualPaymentDate.diffInDays(this.plannedPaymentDate) > 0;
  }

  /** Текст расчётного счёта получателя */
  get receiverBankAccountText(): string {
    if (!this._receiverBankAccount) return 'Не выбрано';
    let result = this._receiverBankAccount.account;
    result = Core.utils.regional.ru.formatBankAccount(result);
    result += ` в ${this._receiverBankAccount.bank?.name ?? ''}`;
    return result;
  }

  /** Текст курса платежа */
  get currencyRateText(): string | null {
    if (!this._paymentType) return null;

    if (this._paymentType.id === 'byRateOfCentralBank') {
      return this._paymentType.name.replace(/РФ/, '');
    }

    if (!this.orderCurrency) return null;

    if (
        this.orderCurrency.code !== this.paymentRequestCurrency?.code
        && this._paymentType?.id !== 'byRateOfCentralBank'
    ) {
      /** Объект курса истинен всегда — спрашиваем само значение.
       *  CurrencyRate.format() уже даёт 4 знака без разделителей разрядов,
       *  внешнее форматирование не нужно. */
      if (this._paymentRequestCurrencyRate.isEmpty()) {
        return '--';
      } else {
        return `${this._paymentRequestCurrencyRate.format()} ${
            this.paymentRequestCurrency?.symbol ?? ''
        }`;
      }
    }

    return null;
  }

  // ============================================================
  // Состояния дат и экспорта для отображения
  // (логика перенесена из date-компонентов, чтобы не дублировать)
  // ============================================================

  /**
   * Состояние фактической даты оплаты:
   *   error   — плановая дата прошла, оплаты нет («Не оплачено»);
   *   warning — оплачено позже плановой («Просрочено»);
   *   success — оплачено в срок («Вовремя»).
   * Если плановой даты нет — состояния нет.
   */
  get actualPaymentDateState(): PaymentDateState {
    if (!DateSimple.isDateSimple(this.plannedPaymentDate)) {
      return { modifier: null, label: null };
    }

    if (!DateSimple.isDateSimple(this.actualPaymentDate)) {
      if (this.plannedPaymentDate.isFuture() === false) {
        return { modifier: 'error', label: 'Не оплачено' };
      }
      else {
        return { modifier: null, label: null };
      }
    }

    if (this.actualPaymentDate.isAfter(this.plannedPaymentDate)) {
      return { modifier: 'warning', label: 'Просрочено' };
    }
    else {
      return { modifier: 'success', label: 'Вовремя' };
    }
  }

  /**
   * Состояние плановой даты оплаты:
   *   error   — плановая дата прошла, не оплачено;
   *   warning — до плановой даты ≤3 дней, не оплачено и не выгружено.
   * Подпись «Не выгружено» показывается, когда платёж не выгружен в Oracle
   * (для error — только если не выгружен, для warning — всегда).
   */
  get plannedPaymentDateState(): PaymentDateState {
    if (!DateSimple.isDateSimple(this.plannedPaymentDate)) {
      return { modifier: null, label: null };
    }

    if (this.actualPaymentDate) {
      return { modifier: null, label: null };
    }

    const diffInDays = this.plannedPaymentDate.diffInDays(DateSimple.today());

    if (diffInDays < 0) {
      const label = !this.isExportedToSWOracle ? 'Не выгружено' : null;
      return { modifier: 'error', label };
    }

    if (diffInDays <= 3 && !this.isExportedToSWOracle) {
      return { modifier: 'warning', label: 'Не выгружено' };
    }

    return { modifier: null, label: null };
  }

  /** Платёж выгружен в Oracle, но ещё не отменён там (ждёт отмены) */
  get isAwaitingCancellation(): boolean {
    return this.isExportedToSWOracle
        && this.isCanceled
        && this.lastStatusText !== 'Платеж отменен'
        && this.lastStatusText !== null;
  }

  /**
   * Состояние экспорта в Oracle для компонента PaymentRequestExportStatus:
   *   error   — ждёт отмены, либо просрочено и не выгружено;
   *   warning — не оплачено, плановая дата в будущем, не выгружено;
   *   success — успешно выгружено.
   * label — текст статуса, icon — material-иконка.
   */
  get exportState(): { modifier: 'error' | 'warning' | 'success' | null; label: string; icon: string } {
    const isExported = this.isExportedToSWOracle;
    const awaiting   = this.isAwaitingCancellation;

    let modifier: 'error' | 'warning' | 'success' | null = null;

    if (awaiting) {
      modifier = 'error';
    }
    else if (
        !this.actualPaymentDate
        && DateSimple.isDateSimple(this.plannedPaymentDate)
        && this.plannedPaymentDate.isFuture() === false
        && !isExported
    ) {
      modifier = 'error';
    }
    else if (
        !this.actualPaymentDate
        && DateSimple.isDateSimple(this.plannedPaymentDate)
        && this.plannedPaymentDate.isFuture()
        && !isExported
    ) {
      modifier = 'warning';
    }
    else if (isExported && !awaiting) {
      modifier = 'success';
    }

    let label: string;
    if (isExported) {
      label = awaiting ? 'Ожидает отмены' : 'Отправлено';
    }
    else {
      label = 'Ждёт отправки';
    }

    const icon = (isExported && !awaiting) ? 'published_with_changes' : 'hourglass_empty';

    return { modifier, label, icon };
  }

  /**
   * Возвращает доступные типы оплаты в зависимости от состояния платежа
   */
  getAvailablePaymentTypes(): PaymentType[] {
    let types = PaymentType.all();

    /** Валюта сделки локальная — конвертировать нечего, и выбора нет: direct
     *  единственно возможен, остальные три описывают конвертацию, которой не
     *  будет. Валюта не локальная — direct, наоборот, недопустим: с курсом 1
     *  он посчитал бы валютную сумму как локальную.
     *
     *  Сравнивается валюта сделки, а не счёта: валюта счёта с ней часто не
     *  совпадает. */
    if (this._orderCurrencyCode === this.actualPaymentCurrencyCode) {
      return types.filter(type => type.id === 'direct');
    } else {
      types = types.filter(type => type.id !== 'direct');
    }

    /** Счёт в локальной валюте при валютной сделке. Тогда сумма платежа уже
     *  написана в счёте, и курс берётся из неё, а не извне. */
    const hasLocalInvoice = this.invoice?.currencyCode === this.actualPaymentCurrencyCode;

    /** «В точности по документу» берёт курс из отгрузки, её учётным курсом.
     *  Без отгрузки брать его неоткуда. */
    if (this._shipment === null || this._shipment === undefined) {
      types = types.filter(type => type.id !== 'byDocumentExactly');
    }

    /** Счёт в локальной валюте — по курсу ЦБ платить не в чем. */
    if (hasLocalInvoice === true) {
      types = types.filter(type => type.id !== 'byRateOfCentralBank');
    }

    /** «Произвольный курс» — там, где курс задаёт пользователь, а не ЦБ и не
     *  документ: счёт выставлен в локальной валюте, а сделка валютная.
     *
     *  Прежнее условие разрешало этот тип только по основанию «счёт
     *  поставщика по отгрузке». Оно пришло из customCurrencyRateDisabled в
     *  форме, где описывало доступность ПОЛЯ ввода курса, а не смысл самого
     *  типа, и отрезало ровно тот случай, ради которого тип и существует. */
    if (hasLocalInvoice === false) {
      types = types.filter(type => type.id !== 'byCustomRate');
    }

    return types;
  }

  // ============================================================
  // Methods
  // ============================================================

  /**
   * Пересобирает текст назначения платежа из текущих данных
   */
  /**
   * Описание документа-основания: «счёта №1 от 01.01.2026», «Отгрузки №5 …».
   * Пустая строка, если основания нет или оно не заполнено.
   *
   * Вынесено из updateReasonText: тот же текст нужен комментарию для оракла,
   * и расходиться этим двум формулировкам нельзя.
   */
  get basisDocumentText(): string {
    if (!this.reasonType) {
      return '';
    }

    let document = '';

    switch (this.reasonType.id) {
      case 'contractInvoice':
      case 'invoiceOffer':
      case 'proformaInvoice':
      case 'invoiceShipment':
      case 'invoiceOrder':
      case 'invoice': {
        const entityName = ['proformaInvoice', 'invoiceOffer', 'contractInvoice'].includes(this.reasonType.id)
            ? this.reasonType.name
            : 'счёту';

        document = entityName;

        if (this.invoice?.number) {
          document += ` №${this.invoice.number}`;
        }
        if (this.invoice?.date) {
          document += ` от ${Core.utils.date.format(this.invoice.date)}`;
        }
        break;
      }

      case 'order': {
        if (this._order) {
          const formattedId = Core.utils.string.formatWithDelimiter(this._order.id);
          document = `заказу №${formattedId}`;

          if (this._order.createdAt) {
            document += ` от ${Core.utils.date.format(this._order.createdAt)}`;
          }
        }
        break;
      }

      case 'shipment': {
        if (this._shipment) {
          const documentType   = this._shipment.documentType?.name ?? this._shipment.documentType ?? '';
          document = `${documentType} №${this._shipment.documentNumber}`;

          if (this._shipment.documentDate) {
            document += ` от ${Core.utils.date.format(this._shipment.documentDate)}`;
          }
        }
        break;
      }

      case 'orderPosition': {
        if (this._orderPosition) {
          const formattedId = Core.utils.string.formatWithDelimiter(this._orderPosition.id);
          document = `заявке №${formattedId}`;

          if (this._orderPosition.createdAt) {
            document += ` от ${Core.utils.date.format(this._orderPosition.createdAt)}`;
          }
        }
        break;
      }
    }

    return document;
  }

  /**
   * Пересобирает текст назначения платежа из текущих данных
   */
  updateReasonText(): void {
    const document = this.basisDocumentText;

    if (!document) {
      this.reasonText = '';
      return;
    }

    if (this.reasonBasis?.value) {
      this.reasonText = `Оплата за "${this.reasonBasis.value}" по ${document}`;
    } else {
      this.reasonText = `Оплата по ${document}`;
    }
  }

  /**
   * Добавляет позицию и пересчитывает суммы.
   * При добавлении первой позиции автоматически подставляет reasonBasis по категории продукта.
   */
  addPosition(position: PaymentRequestPosition | any): void {
    if (!(position instanceof PaymentRequestPosition)) {
      position = PaymentRequestPosition.fromOrderPosition(position);
    }

    /** Обратную ссылку ставит родитель, а не фабрика позиции: валюта и курс
     *  живут здесь, и до этого момента позиции считать нечем. */
    position.setPaymentRequest(this);

    /** Ставка — VatRate, то есть Numeric, и неявное приведение к number он
     *  запрещает: сравнение с нулём напрямую бросает исключение, а проверка
     *  на истинность всегда истинна, потому что это объект. Поэтому признак
     *  считается один раз через API типа и дальше сравнивается как boolean.
     *  isPositive, а не отрицание isZero: пустая ставка не ноль, но и НДС
     *  по ней не считается — внутри greaterThan(0), на NaN это false. */
    const positionHasVat = position.vatRateSeller?.isPositive() === true;

    // Получатель не является плательщиком НДС — позиции с НДС недопустимы
    if (this._receiverCompany?.isVatPayer === false && positionHasVat === true) {
      throw new CriticalValidationError(
          'Получатель не является плательщиком НДС — невозможно добавить позицию с НДС',
          {
            receiverCompany: {
              id: this._receiverCompany?.id,
              isVatPayer: this._receiverCompany?.isVatPayer
            },
            position: {
              orderPosition: position.orderPosition.id,
              vatRateSeller: position.vatRateSeller
            }
          },
      );
    }

    // Получатель является плательщиком НДС — позиции без НДС недопустимы
    if (this._receiverCompany?.isVatPayer === true && positionHasVat === false) {
      throw new CriticalValidationError(
          'Получатель является плательщиком НДС — невозможно добавить позицию без НДС',
          {
            receiverCompany: {
              id: this._receiverCompany?.id,
              isVatPayer: this._receiverCompany?.isVatPayer
            },
            position: {
              orderPosition: position.orderPosition.id,
              vatRateSeller: position.vatRateSeller
            }
          },
      );
    }

    // Первая позиция определяет тип НДС и reasonBasis для всего платежа
    if (this.positions.length === 0) {
      this.isVatIncluded = position.isVatIncludedSeller;

      // Автоподстановка reasonBasis по категории продукта первой позиции
      if (!this.reasonBasis) {
        const categoryId = position.orderPosition?.productCategory?.id;

        if (categoryId) {
          this.applyReasonBasisFromRootCategory(categoryId);
        }
      }
    }

    this.positions.push(position);
    this.initManufactureDate();
    this.recalculateSums();
  }

  /**
   * Подставляет основание платежа из корневой категории. Корень грузится с бэка,
   * поэтому подстановка асинхронная: основание, которое пользователь успел
   * выбрать сам, не перетираем.
   */
  private async applyReasonBasisFromRootCategory(categoryId: number): Promise<void> {
    const rootCategory = await ProductCategoriesService.getRootCategory(categoryId);

    if (Core.utils.isNullOrUndefined(rootCategory?.paymentReasonBasis) === true) {
      // у корня основание не задано — подставлять нечего
    }
    else if (this.hasBasis === true) {
      // основание уже выбрано — не перетираем
    }
    else {
      this.reasonBasis = rootCategory!.paymentReasonBasis;
      this.updateReasonText();
    }
  }

  /**
   * Дата плана производства: первое число месяца, следующего за желаемой
   * датой поставки первой позиции платежа.
   *
   * Фишка ЛСЗ — для остальных партнёров поле не заполняется.
   *
   * Порядок операций важен: сначала число ставится первым, и только потом
   * прибавляется месяц. Наоборот нельзя — 31 марта плюс месяц даёт 1 мая,
   * потому что в апреле нет 31-го, и получился бы послеследующий месяц.
   *
   * Уже заполненное значение не перетирается: вторая и последующие позиции
   * дату не двигают, и ручная правка пользователя тоже сохраняется.
   */
  initManufactureDate(): void {
    if (Core.currentPartner?.isLSZ !== true) {
      return;
    }

    if (this.manufactureDate !== null && this.manufactureDate !== undefined) {
      return;
    }

    const desiredReceiptDate = this.positions[0]?.orderPosition?.desiredReceiptDate;

    if (desiredReceiptDate === null || desiredReceiptDate === undefined) {
      return;
    }

    const firstDayOfMonth = new DateSimple(desiredReceiptDate);
    firstDayOfMonth.setDate(1);

    this.manufactureDate = firstDayOfMonth.addMonths(1);
  }

  /**
   * Раскладывает долю оплаты по позициям. Вызывается контролом «Процент
   * оплаты» и больше нигде: сам процент не хранится ни в модели, ни в БД.
   *
   * Раскладывается деньгами, а не количеством: ведущая величина позиции —
   * сумма, количество из неё выводится. Через количество доля ложилась криво,
   * потому что количество округляется до трёх знаков.
   *
   * База — снимок основания, а не текущее количество позиции: иначе повторное
   * применение процента било бы по уже уменьшенному количеству и давало долю
   * от доли.
   */
  applyPaymentPercent(percent: number): void {
    if (percent === null || percent === undefined) {
      return;
    }

    for (const position of this.positions) {
      position.sumFullSeller = Sum.fromOperation(position.basisSumFullSeller.value.times(percent).div(100));
    }
  }

  /**
   * Текущая доля оплаты документа, в процентах. Выводится из снимка
   * основания, поэтому переживает и редактирование заказа, и повторное
   * открытие платежа.
   */
  get paymentSharePercent(): number | null {
    let basis = Sum.zero();
    let paid  = Sum.zero();

    for (const position of this.positions) {
      basis = basis.plus(position.basisSumSeller);
      paid  = paid.plus(position.sumSeller);
    }

    if (basis.isZero() === true) {
      return null;
    } else {
      /** asPercent у Multiplier дал бы отклонение от базы, а нужна сама доля:
       *  30% вместо минус 70. Поэтому берём значение множителя как есть. */
      return paid.divBySum(basis).value.times(100).toNumber();
    }
  }

  /**
   * Удаляет позицию и пересчитывает суммы.
   * Принимает саму позицию PaymentRequestPosition или позицию заказа (orderPosition).
   */
  deletePosition(position: PaymentRequestPosition | any): void {
    let target: PaymentRequestPosition | undefined;

    if (position instanceof PaymentRequestPosition) {
      target = this.positions.find(p => p === position);
    } else {
      // Передана позиция заказа — ищем по orderPosition.id
      target = this.positions.find(p => p.orderPosition?.id === position.id);
    }

    if (!target) return;

    const index = this.positions.indexOf(target);
    if (index !== -1) {
      this.positions.splice(index, 1);
      this.recalculateSums();
    }
  }

  /**
   * Пересчитывает суммы документа по позициям.
   * Финальное округление не нужно: конструктор Numeric нормализует значение
   * до SCALE, каждый plus рождает результат уже округлённым.
   */
  /**
   * Курс задаёт пользователь, а не документ.
   *
   * Основной случай: счёт в локальной валюте по валютной сделке. Сумма в
   * счёте уже написана, значит курс в нём фактически зафиксирован, и брать
   * курс ЦБ нельзя — платёж разойдётся с бумагой. Отдельного поля суммы
   * счёта нет: пользователь вводит её в позицию, в «Сумму полную», и из
   * неё выводится курс. Либо вводит курс напрямую, в поле под типом.
   */
  get isCurrencyRateUserDefined(): boolean {
    return this._paymentType?.id === 'byCustomRate';
  }

  /**
   * Курс платежа известен.
   *
   * Пустой курс — это не ноль и не единица, а «ещё не посчитан»: форма такое
   * поле просто не показывает, чтобы не предлагать смотреть на пустоту.
   */
  get hasCurrencyRate(): boolean {
    return this._paymentRequestCurrencyRate?.isPositive() === true;
  }

  /**
   * Выводит курс из суммы счёта: её отношение к сумме основания платежа.
   *
   * Курс - величина одна на документ, и браться должна из документа. Раньше
   * её добывали из первой позиции, где сумма введена руками: хранить сумму
   * счёта было негде. Из-за этого курс зависел от того, какую позицию
   * правили первой и в какой момент ввод считался законченным - первая же
   * набранная цифра его фиксировала.
   *
   * Делим на сумму основания ЭТОГО платежа, а не всего документа: счёт
   * бывает выставлен на часть отгрузки, и тогда и сумма, и основание - про
   * эту же часть.
   *
   * Считается в момент ввода суммы счёта и при выборе типа оплаты, но не на
   * каждом пересчёте итогов. Курс - свойство счёта, а не того, какую часть
   * основания мы решили оплатить: иначе правка позиции сдвигала бы курс,
   * хотя в счёте ничего не менялось. Надо пересчитать - правится сумма
   * счёта или сам курс.
   *
   * Курс строится из Decimal напрямую: divBySum дал бы Multiplier со своим
   * scale 4, а у курса их восемь.
   */
  applyCurrencyRateFromInvoice(): void {
    if (this.isCurrencyRateUserDefined === false) {
      return;
    }
    else if (this.invoice?.sum?.isPositive() !== true) {
      return;
    }
    else if (this.sumFull.isPositive() === false) {
      return;
    }
    else {
      const rate = new CurrencyRate(this.invoice.sum.value.div(this.sumFull.value));

      /** Без сверки получился бы цикл: сеттер курса пересчитывает позиции,
       *  пересчёт зовёт итоги, итоги — снова сюда. Сумма основания от курса
       *  не зависит, поэтому второй заход даёт тот же курс и останавливается. */
      if (this._paymentRequestCurrencyRate?.equals(rate) === true) {
        return;
      }

      this.paymentRequestCurrencyRate = rate;
    }
  }

  /**
   * Пользователь ввёл сумму в позицию. Курс отсюда больше не выводится - он
   * считается от суммы счёта, - остаются итоги документа.
   */
  onPositionSumEntered(): void {
    this.recalculateSums();
  }

  /**
   * Почему нельзя править полную сумму позиции, либо null.
   *
   * «В точности по документу» означает разное в зависимости от основания.
   * С отгрузкой суммы берутся из неё готовыми, и правка их только ломает.
   * Со счётом наоборот: введённая сумма задаёт курс платежа, и поле обязано
   * быть доступным — иначе курс остаётся единицей, а форма не заполняется.
   *
   * Прежде карточка гасила поле по одному лишь типу оплаты, не различая эти
   * два случая.
   */
  get reasonUnavailableEditPositionSum(): string | null {
    if (this.isEditable === false) {
      return this.editDisabledReason;
    }
    else if (this._paymentType?.id === 'byDocumentExactly') {
      return 'Суммы берутся из документа основания';
    }
    else {
      return null;
    }
  }

  /**
   * Почему платёж нельзя сохранить, либо null.
   *
   * Правила живут здесь, а не в карточке: они про платёж, и по ним же
   * проверяет бэк. Карточка берёт отсюда и запрет на кнопку, и текст над
   * футером — раньше запрет и объяснение жили порознь, поэтому кнопка
   * гасла молча во всех случаях, кроме незаполненного основания.
   *
   * Порядок проверок не случаен: пустой курс делает пустыми и суммы, так
   * что без него первой сработала бы жалоба на сумму и увела в сторону.
   */
  /**
   * По документу-основанию платить нечего: позиции собрались, но остаток у
   * всех нулевой, то есть документ оплачен целиком.
   *
   * Пустой список позиций сюда не относится — это «ещё не выбрали основание»,
   * и говорит об этом другая причина.
   */
  get isBasisFullyPaid(): boolean {
    if (this.positions.length === 0) {
      return false;
    }
    else {
      return this.positions.every(position => position.sumFullSeller.isPositive() === false);
    }
  }

  get reasonUnavailableSubmit(): string | null {
    if (this.hasBasis === false) {
      return 'Не указано что оплачивается';
    }
    else if (this.isExisting === true && this.isEditable === false) {
      return this.editDisabledReason ?? 'Платёж больше нельзя изменить';
    }
    else if (this.isBasisFullyPaid === true) {
      return 'По этому документу платить нечего: все позиции уже оплачены';
    }
    else if (this.isCurrencyRateUserDefined === true && this._paymentRequestCurrencyRate?.isPositive() !== true) {
      return 'Не задан курс: укажите сумму счёта — курс рассчитается от неё';
    }
    else if (this.hasSum === false) {
      return 'Сумма платежа не заполнена';
    }
    else {
      return null;
    }
  }

  recalculateSums(): void {
    this.sumFull               = Sum.zero();
    this.vatSum                = Sum.zero();
    this.paymentRequestSumFull = Sum.zero();
    this.paymentRequestVatSum  = Sum.zero();

    for (const position of this.positions) {
      this.sumFull = this.sumFull.plus(position.sumFullSeller);

      if (position.vatSumSeller === null || position.vatSumSeller === undefined) {
        // Позиция без НДС — вклад в НДС документа нулевой
      } else {
        this.vatSum = this.vatSum.plus(position.vatSumSeller);
      }

      if (position.paymentRequestSumFull === null || position.paymentRequestSumFull === undefined) {
        this.paymentRequestSumFull = this.paymentRequestSumFull.plus(position.paymentRequestSumFullCalculated);
      } else {
        this.paymentRequestSumFull = this.paymentRequestSumFull.plus(position.paymentRequestSumFull);
      }

      if (position.paymentRequestVatSum === null || position.paymentRequestVatSum === undefined) {
        // Позиция без НДС — вклад в НДС заявки нулевой
      } else {
        this.paymentRequestVatSum = this.paymentRequestVatSum.plus(position.paymentRequestVatSum);
      }
    }
  }

  /**
   * Перестраивает позиции из текущего основания.
   *
   * Только для несохранённого платежа. У загруженного документ-основание
   * приезжает заглушкой: у заказа это id с датой создания, у отгрузки тип,
   * номер и дата документа — позиций в них нет. Пересборка по такой
   * заглушке очистила бы позиции платежа, а то и упала бы на обходе
   * несуществующего массива.
   *
   * Пересобирать их там и незачем: позиции сохранённого платежа пришли с
   * сервера и живут своей жизнью, основание у него уже не меняется.
   *
   * Вызывается из сеттера типа оплаты, а тот — из сеттера валюты сделки и
   * из формы. Сейчас все три контрола на редактировании закрыты, но защита
   * эта шаблонная: снимут дизейбл ради другой задачи — и позиции исчезнут.
   */
  private rebuildPositions(): void {
    if (this.isExisting === true) {
      return;
    }

    this.positions     = [];
    this.isVatIncluded = null;

    /** Источник — документ, который требует текущий тип основания. Проверка
     *  на сам документ недостаточна: модель держит и прежние, и без неё
     *  позиции собрались бы разом из отгрузки и заказа. */
    if (this._shipment && this._reasonType?.requiresShipment === true) {
      for (const shipmentPosition of this._shipment.positions) {
        this.addPosition(PaymentRequestPosition.fromShipmentPosition(shipmentPosition));
      }
    }

    if (this._order && this._reasonType?.requiresOrder === true) {
      for (const orderPosition of this._order.positions) {
        /** Мультивалютный заказ: в платёж идут только позиции в валюте сделки.
         *  Раньше отбор жил в форме и работал лишь для рублёвой ветки, а в
         *  валютной позиции попадали все подряд. Пока валюта не выбрана,
         *  ограничивать нечем - берём всё. */
        const isPositionCurrencyMatched = this._orderCurrencyCode === null
            || this._orderCurrencyCode === undefined
            || orderPosition.currencyCode === this._orderCurrencyCode;

        if (isPositionCurrencyMatched === true) {
          this.addPosition(PaymentRequestPosition.fromOrderPosition(orderPosition));
        }
        else {
          // Позиция в другой валюте — в этот платёж не входит
        }
      }
    }

    if (this._orderPosition && this._reasonType?.requiresOrderPosition === true) {
      this.addPosition(PaymentRequestPosition.fromOrderPosition(this._orderPosition));
    }
  }

  /**
   * Сериализация для сервера. Отличие от базовой одно: позиции с нулевым
   * количеством не уходят - платить по ним нечего. В карточке они остаются,
   * по ним видно, что позиция уже оплачена целиком.
   */
  toJSON(fieldsToSimplify: string[] = []): Record<string, any> {
    const result = super.toJSON(fieldsToSimplify);

    result.positions = this.positions.filter(position => position.qty.isPositive() === true);

    /** Документ-основание уходит только тот, который требует тип. Прежние
     *  модель помнит нарочно — чтобы переключение типа не стирало выбор, —
     *  но платежу их знать незачем. */
    if (this._reasonType?.requiresShipment !== true) {
      result.shipment = null;
    }

    if (this._reasonType?.requiresOrder !== true) {
      result.order = null;
    }

    if (this._reasonType?.requiresOrderPosition !== true) {
      result.orderPosition = null;
    }

    return result;
  }

  /**
   * Валидация перед сохранением
   */
  validate(): string[] {
    const errors: string[] = [];

    if (!this.receiver) {
      errors.push('Не указан получатель');
    }

    if (!this.receiverCompany) {
      errors.push('Не указано юр. лицо получателя');
    }

    if (!this.isForeign && !this.receiverBankAccount) {
      errors.push('Не указан расчётный счёт');
    }

    if (Core.utils.isNullOrUndefined(this._order) === false && this._order.isMultiCurrency === true && this._reasonType?.requiresOrder === true
        && Core.utils.isNullOrUndefined(this._orderCurrencyCode) === true) {
      errors.push('Не выбрана валюта заказа, в которой создаётся платёж');
    }

    if (this.positions.length === 0) {
      errors.push('Не добавлены позиции');
    }

    if (this.sumFull.isEmpty() || this.sumFull.isZero()) {
      errors.push('Сумма платежа равна нулю');
    }

    /** Проверки курса — цепочкой: каждая следующая имеет смысл только
     *  при валидном результате предыдущей, а «Не указан курс оплаты»
     *  иначе выпало бы дважды. */
    if (this.paymentRequestCurrencyRate.isEmpty()) {
      errors.push('Не указан курс оплаты');
    }
    else if (!this.paymentRequestCurrencyRate.isPositive()) {
      errors.push('Некорректный курс валюты');
    }
    else if (this.isSameCurrency && !this.paymentRequestCurrencyRate.equals(new CurrencyRate(1))) {
      errors.push('Валюта заказа и оплаты совпадает, но курс не равен 1');
    }
    else if (!this.isSameCurrency && this.paymentRequestCurrencyRate.equals(new CurrencyRate(1))) {
      errors.push('Не указан курс оплаты');
    }

    if (this.isManual && !this.externalNumber) {
      errors.push('Не заполнен номер КСЗ');
    }

    if (!this._productionSite) {
      errors.push('Не указана производственная площадка');
    }

    return errors;
  }

  /**
   * Генерирует текст комментария для поля o_comment в oracle.etp_payments
   * Для наманганских площадок (nmg_) добавляет детализацию по позициям
   */
  generateOracleComment(): string {
    const lines: string[] = [];

    if (this.isNamanganSite === false) {
      if (this._productionSite?.name) {
        lines.push(this._productionSite.name);
      }

      return lines.join('\n');
    }

    /** Шапка: доля оплаты, за что и по какому документу. Процент считается из
     *  снимка основания — своего поля у него нет. Хвостовые нули срезаются:
     *  «30%», но «33.33%». */
    const sharePercent = this.paymentSharePercent;
    const shareText    = sharePercent === null ? '' : `${Number(sharePercent.toFixed(2))}% `;
    const basisText    = this.reasonBasis?.value ?? '';
    const documentText = this.basisDocumentText;

    let header = `Оплата ${shareText}`.trimEnd();

    if (basisText !== '') {
      header += ` за ${basisText}`;
    }
    if (documentText !== '') {
      header += ` по ${documentText}`;
    }

    lines.push(header);

    if (this._productionSite?.name) {
      lines.push(`Площадка: ${this._productionSite.name}`);
    }

    if (this.positions.length > 0) {
      lines.push('Товар:');

      for (const position of this.positions) {
        const productName = position.orderPosition?.product?.name
            ?? position.orderPosition?.name
            ?? '—';
        const qty      = position.qty;
        const unit     = position.orderPosition?.unit?.name ?? '';
        const price    = position.priceFullSeller;
        const vatRate  = position.vatRateSeller;
        const sum      = position.paymentRequestSumFull ?? position.paymentRequestSumFullCalculated;

        /** Цена — в валюте сделки, сумма — в валюте платежа. Знак процента
         *  печатает сам VatRate, второй здесь давал «12.00%%». */
        const priceCurrency = this._orderCurrencyCode ?? '';
        const sumCurrency   = this._paymentRequestCurrencyCode ?? '';

        /** Нулевая ставка — это «без НДС», а не «с НДС 0.00%». Тем же
         *  признаком помечена и сумма: она полная, но без ставки налога в
         *  ней нет, и подпись «с НДС» на ней сбивала бы с толку. */
        let vatText: string;
        let sumVatText: string;

        if (vatRate?.isPositive() === true) {
          vatText    = `с НДС ${vatRate}`;
          sumVatText = 'с НДС';
        }
        else {
          vatText    = 'без НДС';
          sumVatText = 'без НДС';
        }

        lines.push(
            `${productName} - ${qty} - ${unit} - ${price} ${priceCurrency} ${vatText} - ${sum} ${sumCurrency} ${sumVatText}`
        );
      }

      const totalSum      = this.paymentRequestSumFull;
      const totalVat      = this.paymentRequestVatSum;
      const totalCurrency = this._paymentRequestCurrencyCode ?? '';
      const vatRate       = this.positions[0]?.vatRateSeller;

      let totalVatText: string;

      if (vatRate?.isPositive() === true) {
        totalVatText = `с НДС ${vatRate}`;
      }
      else {
        totalVatText = 'без НДС';
      }

      let total = `Итого: ${totalSum} ${totalCurrency} ${totalVatText}`;

      if (totalVat.isPositive() === true) {
        total += `, НДС: ${totalVat} ${totalCurrency}`;
      }

      lines.push(total);
    }

    return lines.join('\n');
  }

  // ============================================================
  // Factory methods
  // ============================================================

  /**
   * Создаёт платёж на основании отгрузки
   */
  static fromShipment(shipment: Shipment, options?: {
    receiver?: Partner;
    receiverCompany?: Company;
    reasonType?: PaymentReasonType;
  }): PaymentRequest {
    /** Через fromNew, а не new: суммы и курс инициализируются там, и
     *  addPosition ниже сразу вызывает recalculateSums. */
    const pr = PaymentRequest.fromNew();

    // Устанавливаем получателя (триггерит каскад)
    if (options?.receiver) {
      pr.receiver = options.receiver;
    }
    if (options?.receiverCompany) {
      pr.receiverCompany = options.receiverCompany;
    }

    /** Отгрузки требует не только основание «Отгрузка», но и «Счёт поставщика
     *  по отгрузке». Когда тип уже выбран пользователем, он приходит сюда и
     *  не затирается: иначе вместе с ним пропадали бы реквизиты счёта. */
    pr.reasonType = options?.reasonType ?? PaymentReasonType.SHIPMENT;
    pr.shipment   = shipment;

    // Валюта из отгрузки
    if (shipment.orderCurrencyCode) {
      pr.orderCurrencyCode = shipment.orderCurrencyCode;
    }

    /** Позиции здесь НЕ добавляются: их уже собрал сеттер shipment через
     *  rebuildPositions. Повторный цикл давал в платеже по две одинаковые
     *  позиции на каждую позицию отгрузки и, соответственно, удвоенный итог. */

    return pr;
  }

  /**
   * Создаёт платёж на основании заказа.
   * При нескольких валютах позиции появятся после выбора валюты сделки.
   */
  static fromOrder(order: Order, options?: {
    receiver?: Partner;
    receiverCompany?: Company;
    reasonType?: PaymentReasonType;
  }): PaymentRequest {
    /** Через fromNew, а не new: суммы и курс инициализируются там, и
     *  addPosition сразу вызывает recalculateSums. */
    const pr = PaymentRequest.fromNew();

    /** Получателя ставим до позиций: addPosition проверяет позицию на НДС
     *  по юр. лицу получателя. */
    if (options?.receiver) {
      pr.receiver = options.receiver;
    }
    if (options?.receiverCompany) {
      pr.receiverCompany = options.receiverCompany;
    }

    /** Тип основания приходит снаружи, если его уже выбрал пользователь:
     *  заказа требует и «Счёт поставщика по заказу». См. fromShipment. */
    pr.reasonType = options?.reasonType ?? PaymentReasonType.ORDER;
    pr.order      = order;

    // Площадка из первой позиции — как в fromShipment
    const firstPosition = order.positions?.[0];
    if (firstPosition?.buyerProductionSite) {
      pr.productionSite = firstPosition.buyerProductionSite;
    }

    return pr;
  }

  /**
   * Создаёт платёж на основании позиции заказа.
   *
   * Валюту, режим НДС и текст назначения проставляет сеттер orderPosition —
   * здесь они не дублируются. Позиция всегда одна: основание «позиция заказа»
   * платит ровно за неё.
   */
  static fromOrderPosition(orderPosition: OrderPosition, options?: {
    receiver?: Partner;
    receiverCompany?: Company;
    reasonType?: PaymentReasonType;
  }): PaymentRequest {
    /** Через fromNew, а не new: суммы и курс инициализируются там, и
     *  addPosition ниже сразу вызывает recalculateSums. */
    const pr = PaymentRequest.fromNew();

    /** Получателя ставим до addPosition: он проверяет позицию на НДС
     *  по юр. лицу получателя. */
    if (options?.receiver) {
      pr.receiver = options.receiver;
    }
    if (options?.receiverCompany) {
      pr.receiverCompany = options.receiverCompany;
    }

    /** Тип основания приходит снаружи, если его уже выбрал пользователь.
     *  См. fromShipment. */
    pr.reasonType     = options?.reasonType ?? PaymentReasonType.ORDER_POSITION;
    pr.orderPosition  = orderPosition;

    // Площадка из позиции — как в fromShipment, где она берётся из первой позиции
    if (orderPosition.buyerProductionSite) {
      pr.productionSite = orderPosition.buyerProductionSite;
    }

    /** Позиция здесь НЕ добавляется: её уже добавил сеттер orderPosition
     *  через rebuildPositions. См. fromShipment — та же причина. */

    return pr;
  }
}

export default PaymentRequest;
